import './final-sell.scss'
import { useMemo, useRef, useState, useCallback } from 'react'
import CommonModal from '@/components/CommonModal/CommonModal'
import { useStore } from '@/store'
import { useLocal } from '@/i18n/LocalContext'
import appExpose from '@app/expose'
import { useMaxWidth } from '@/hooks/useMaxWidth'
import { $alert } from '@/utils'
import { createPlayerName, getColorStyle } from '@app/utils'
import MarketBtn from '../MarketBtn/MarketBtn'
import CountController from '../CountController/CountController'
import { useSell } from '@app/hooks/useSell'
import { sell } from '@app/request'
import { FinalSellData } from '@app/store/sell'
import useSellData from '@app/hooks/useSellData'
import useReceive from '@app/hooks/useReceive'
import goldIcon from '../../assets/static-img/gold2.png'
import Tooltips from '@/components/Tooltips/Tooltips'

const { emitter } = appExpose

const getUTCDate = (serverTime: number) => {
  if (serverTime <= 0) return 0

  return new Date(serverTime).getUTCDate()
}

export default function FinalSell() {
  const { $t } = useLocal()
  const { state, setState, dispatch } = useStore()
  const { openReceiveModal } = useReceive()
  const localTime = state.localTime
  const serverTime = state.serverTime * 1000
  const currentDate = useMemo(() => {
    if (serverTime <= 0) return 0

    return getUTCDate(serverTime)
  }, [serverTime])
  const { count, setCount } = useSell()
  const disabledStyle = useMemo(
    () => (count <= 0 ? { filter: 'grayscale(80%)' } : {}),
    [count]
  )
  const finalSellModalOpen = state.finalSellModalOpen
  const playerId = state.login.userInfo.playerId
  const finalSellData = state.finalSellData
  const source = finalSellData.source
  const sellItem = finalSellData.data
  const tree = finalSellData.tree
  const total = useMemo(() => {
    if (!sellItem) return 0

    return Math.min(tree, sellItem.overage)
  }, [tree, sellItem])
  const playerName = useMemo(() => {
    if (!sellItem) return ''

    return createPlayerName(sellItem)
  }, [sellItem])
  const { safeAreaTop } = useMaxWidth()
  const coinStyle = useMemo(() => {
    const style = { transform: `translateY(${safeAreaTop}px)` }
    return style
  }, [safeAreaTop])
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(pending)
  const { pending: sellDataPending, fetchSellData } = useSellData()

  const handleSell = useCallback(async () => {
    if (pendingRef.current) return

    if (count <= 0) {
      return
    }

    if (!sellItem) return

    const check = await new Promise<boolean>((resolve) => {
      $alert($t('确认出售圣诞树？'), {
        rejectText: $t('取消'),
        rejectClose: true,
        onResolve() {
          resolve(true)
        },
        onReject() {
          resolve(false)
        },
      })
    })
    if (!check) return

    let hints = $t('集市收购价已刷新，请重新发起出售')
    const nowDate = getUTCDate(Date.now() - localTime + serverTime)
    const overdue = nowDate > 0 && currentDate > 0 && nowDate !== currentDate
    const sellout = !sellItem || sellItem.overage <= 0
    if (!overdue && sellout) {
      hints = $t('当前玩家集市已售罄')
    }
    const _back = () => {
      dispatch({
        type: 'setState',
        data: {
          finalSellModalOpen: false,
          sellListModalOpen: true,
        },
      })
      emitter.emit('updateSellListData', { refresh: false })
    }
    if (overdue || sellout) {
      if (source === 'search' && overdue) {
        const data = await fetchSellData()
        if (data && data.list[0]) {
          dispatch({
            type: 'setState',
            data: {
              finalSellData: {
                ...finalSellData,
                data: data.list[0],
              } as FinalSellData,
            },
          })
          const check = await new Promise<boolean>((resolve) => {
            $alert(
              $t('玩家集市收购价已刷新为：XXX，确定继续出售？').replace(
                'XXX',
                String(data.list[0].price)
              ),
              {
                rejectText: $t('取消'),
                rejectClose: true,
                onReject() {
                  resolve(false)
                },
                onResolve() {
                  resolve(true)
                },
              }
            )
          })
          if (!check) return
        }
      } else {
        $alert(hints, {
          resolveClose: true,
          onResolve() {
            if (source === 'market') {
              _back()
            }
          },
        })
        return
      }
    }

    pendingRef.current = true
    setPending(pendingRef.current)
    const data = await sell({
      playerId,
      source,
      uid: sellItem.uid,
      num: count,
    })
    if (!data) {
      if (source === 'market') {
        _back()
      } else {
        const _data = await fetchSellData()
        if (_data && _data.list[0]) {
          dispatch({
            type: 'setState',
            data: {
              finalSellData: {
                ...finalSellData,
                data: _data.list[0],
              } as FinalSellData,
            },
          })
        }
      }
    } else {
      // 獎勵彈窗
      openReceiveModal(
        [
          {
            count: sellItem.price * count,
            name: $t('庆典币'),
            desc: $t(
              '圣诞礼盒货币道具，可在圣诞礼盒活动中进行消耗刷新或购买道具'
            ),
            icon: goldIcon,
            key: 'gold',
          },
        ],
        true
      )
      const newTree = tree - count
      dispatch({
        type: 'setState',
        data: {
          finalSellData: {
            source,
            tree: 0,
            data: null,
          } as FinalSellData,
        },
      })
      emitter.emit('updateMarketData')
      if (source === 'market') {
        if (newTree > 0) {
          _back()
        } else {
          dispatch({
            type: 'setState',
            data: {
              finalSellModalOpen: false,
            },
          })
          emitter.emit('updateSellListData', { refresh: false })
        }
      } else {
        if (newTree > 0) {
          dispatch({
            type: 'setState',
            data: {
              targetPlayerId: '',
              finalSellModalOpen: false,
              sellModalOpen: true,
              specifiedSellData: {
                list: [],
                tree: newTree,
              },
            },
          })
        } else {
          dispatch({
            type: 'setState',
            data: {
              finalSellModalOpen: false,
              targetPlayerId: '',
              specifiedSellData: {
                list: [],
                tree: newTree,
              },
            },
          })
        }
      }
    }
    pendingRef.current = false
    setPending(pendingRef.current)
  }, [
    playerId,
    source,
    tree,
    sellItem,
    finalSellData,
    count,
    currentDate,
    localTime,
    serverTime,
  ])

  const onClose = useCallback(() => {
    const data: Parameters<typeof setState>[0] = {
      finalSellModalOpen: false,
    }
    if (finalSellData.source === 'market') {
      data.sellListModalOpen = true
    } else {
      data.sellModalOpen = true
    }
    setState(data)
  }, [finalSellData])

  return (
    <CommonModal
      open={finalSellModalOpen}
      onClose={onClose}
      title={$t('出售圣诞树')}
      type="sell"
    >
      {[
        <div key="1" className="final-sell">
          <div className="sell-search-cutline"></div>
          {!sellDataPending && sellItem ? (
            <div className="sell-search-result">
              <div className="sell-search-name">
                <span>{playerName}</span>
                <span>{playerName}</span>
              </div>
              <div className="sell-search-tree">
                <p className="sell-search-price">
                  {$t('今日收购价：')}
                  <span
                    className="sell-search-price__coin"
                    style={getColorStyle(sellItem)}
                  >
                    {sellItem.price}
                  </span>
                </p>
              </div>
              <CountController
                total={total}
                value={count}
                onChange={setCount}
              />
              <p className="sell-computed-price">
                {$t('收获：')}
                <span className="sell-computed-price__coin">
                  {sellItem.price * count}
                </span>
              </p>
              <div className="sell-search-cutline"></div>
              <MarketBtn type={2} onClick={handleSell} style={disabledStyle}>
                {$t('出售')}
              </MarketBtn>
            </div>
          ) : null}
          {pending || sellDataPending ? (
            <div key="11" className="__onemt__loader-box">
              <div className="__onemt__loader"></div>
            </div>
          ) : null}
        </div>,
        <div key="2" className="coin-block" style={coinStyle}>
          <Tooltips
            title={$t('圣诞树')}
            content={$t('圣诞集市活动道具，可在圣诞集市活动中进行售卖')}
            place="right-start"
            ckey="finalsellcointree123"
            className="coin tree"
          >
            <span>{finalSellData.tree}</span>
          </Tooltips>
        </div>,
      ]}
    </CommonModal>
  )
}
